import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useLazyViewSolarPlantStatusesQuery } from "../../../redux/api/dashboard/dashboardAPI";
import { useLazyViewGenerationServiceProvidersQuery } from "../../../redux/api/generation/generationAPI";
import SitesErrorLogTable from "../../../shared/components/site-error-log-table/site-error-log-table";
import IViewAllGenerationServiceProvidersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-all-generation-service-providers-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { EAlertSeverity } from "../../../shared/oversight-core/enums/alert-severity";
import { EAlertState } from "../../../shared/oversight-core/enums/alert-state";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { IAlertOptions } from "../../../shared/oversight-core/interfaces/alert-options";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { IGenerationServiceProviderView } from "../../../shared/oversight-core/interfaces/generation-service-provider-view";
import { IGoodWeSolarPlantAlert } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-alert";
import { IGroWattSolarPlantAlert } from "../../../shared/oversight-core/interfaces/gro-watt-solar-plant-alert";
import { IPowerHubSolarPlantAlert } from "../../../shared/oversight-core/interfaces/powerhub-solar-plant-alert";
import { IScoutSolarPlantAlert } from "../../../shared/oversight-core/interfaces/scout-solar-plant-alert";
import { ISolarPlantAlerts } from "../../../shared/oversight-core/interfaces/solar-plant-alerts";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import ArrowButton from "../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import ButtonWithBadge from "../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { getServiceProviderLogo } from "../../../shared/oversight-core/utils/get-service-provider-logo";
import SitesErrorLogFilterPanel from "./sites-error-log-filter-panel/sites-error-log-filter-panel";
import {
  getAlertDescription,
  getAlertSeverityLabel,
  getAlertStartTime,
  getAlertStatusLabel,
  getProviderLogoSize,
  ISitesErrorLog,
} from "./sites-error-log-helpers";

export interface IErrorLogFilters {
  generationServiceProviderId: string;
  searchValue: string;
  severity: EAlertSeverity[];
  alertState: EAlertState;
  alertName: string[];
  // ISO instants bounding when an alert started; empty for no date filter.
  from: string;
  to: string;
}

export const initialErrorLogFilters: IErrorLogFilters = {
  generationServiceProviderId: "",
  searchValue: "",
  severity: [],
  alertState: EAlertState.OPEN,
  alertName: [],
  from: "",
  to: "",
};

const mapErrorLogRow = (
  item: ISitesErrorLog,
  serviceProvider: ESolarServiceProvider,
  navigate: NavigateFunction,
) => {
  const logoSize = getProviderLogoSize(serviceProvider);

  return {
    data: [
      <span key="start-time">{getAlertStartTime(item, serviceProvider)}</span>,
      <Row key="site" className="align-items-center row-cols-auto">
        <Col className="pe-0">
          <img
            src={getServiceProviderLogo(serviceProvider)}
            alt=""
            width={logoSize.width}
            height={logoSize.height}
          />
        </Col>
        <Col className="ps-2 text-light font-weight-400 font-size-12">
          {item.plantName}
        </Col>
      </Row>,
      <span key="description">
        {getAlertDescription(item, serviceProvider)}
      </span>,
      <span key="severity">{getAlertSeverityLabel(item)}</span>,
      <Row key="status" className="justify-content-between me-2">
        <Col>{getAlertStatusLabel(item, serviceProvider)}</Col>
        <Col className="col-auto">
          <MaterialIcon
            icon="arrow_right_alt"
            color="#1A3C84"
            size={18}
            weight={500}
          />
        </Col>
      </Row>,
    ],
    cellWidths: [150, 250, 450, 100, 150],
    isActive: false,
    onClick: () => {
      navigate(AppRoute.ADMIN_SITES + "/" + item.plantIdentity, {
        state: {
          id: item.plantIdentity,
          serviceProvider,
          name: item.plantName,
        },
      });
    },
  };
};

const SitesErrorLog = () => {
  const navigate = useNavigate();

  const [solarPlantStatuses, setSolarPlantStatuses] = useState<
    ISitesErrorLog[]
  >([]);
  const [numberOfAlerts, setNumberOfAlerts] = useState(0);
  const [alertOptions, setAlertOptions] = useState<IAlertOptions>();
  const [showFilteringSidePanel, setShowFilteringSidePanel] = useState(false);
  const [filterBadgeValue, setFilterBadgeValue] = useState(0);
  const [solarBrandList, setSolarBrandList] = useState<Option[]>([
    { value: "", label: "", data: "" },
  ]);
  const [filters, setFilters] = useState<IErrorLogFilters>({
    ...initialErrorLogFilters,
  });
  const [searchText, setSearchText] = useState("");

  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();
  const [triggerGenerationServiceProviders] =
    useLazyViewGenerationServiceProvidersQuery();

  // Every filter change merges into the rest, so searching keeps the panel's filters and the panel
  // keeps the search.
  const updateFilters = (changes: Partial<IErrorLogFilters>) => {
    setFilters((previous) => ({ ...previous, ...changes }));
  };

  const onSearch = () => {
    if (searchText) {
      updateFilters({ searchValue: searchText });
    }
  };

  useEffect(() => {
    triggerSolarPlantStatuses({
      extended: true,
      searchValue: filters.searchValue || undefined,
      generationServiceProviderId:
        filters.generationServiceProviderId || undefined,
      severity: filters.severity,
      alertState: filters.alertState,
      alertName: filters.alertName,
      from: filters.from || undefined,
      to: filters.to || undefined,
    })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        const alerts: ISitesErrorLog[] = res.solarPlantStatuses.flatMap(
          (site) =>
            site.solarPlantAlerts.map((alert) => ({
              plantName: site.plantName,
              plantTimeZone: site.plantTimeZone,
              plantIdentity: site.plantIdentity,
              generationServiceProviderId:
                site.generationServiceProvider.identity,
              vrmAlert: alert as ISolarPlantAlerts,
              goodWeAlert: alert as IGoodWeSolarPlantAlert,
              groWattAlert: alert as IGroWattSolarPlantAlert,
              powerHubAlert: alert as IPowerHubSolarPlantAlert,
              scoutAlert: alert as IScoutSolarPlantAlert,
              normalized: alert.normalized,
            })),
        );
        setSolarPlantStatuses(alerts);
        setNumberOfAlerts(res.numberOfAlerts ?? alerts.length);
        setAlertOptions(res.alertOptions);
      })
      .catch(() => {
        setSolarPlantStatuses([]);
        setNumberOfAlerts(0);
      });
  }, [triggerSolarPlantStatuses, filters]);

  useEffect(() => {
    triggerGenerationServiceProviders()
      .unwrap()
      .then((res: IViewAllGenerationServiceProvidersResponseDTO) => {
        const mappingSolarBrandList = res.generationServiceProviders.map(
          (g: IGenerationServiceProviderView) => ({
            value: g.identity,
            label: g.name,
            data: g.shortName,
          }),
        );
        setSolarBrandList(mappingSolarBrandList);
      })
      .catch(() => {
        setSolarBrandList([]);
      });
  }, [triggerGenerationServiceProviders]);

  useEffect(() => {
    if (!searchText) {
      setFilters((previous) => ({ ...previous, searchValue: "" }));
    }
  }, [searchText]);

  const getServiceProvider = (id: string): ESolarServiceProvider => {
    const findServiceProvider = solarBrandList.find(
      (item) => item.value === id,
    );

    const serviceProvider =
      findServiceProvider?.data?.toUpperCase() as ESolarServiceProvider;

    return serviceProvider;
  };

  return (
    <div className="position-relative">
      <Row className="align-items-center mt-3">
        <Col className="col-auto">
          <Row className="mx-0 align-items-center">
            <Col className="col-auto">
              <ArrowButton onBack={() => navigate(-1)} />
            </Col>
            <Col className="col-auto text-dark font-weight-500 font-size-24 ps-3">
              Error Log
            </Col>
            <Col className="col-auto text-light font-size-14 ps-2">
              {numberOfAlerts} {numberOfAlerts === 1 ? "alert" : "alerts"}
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-sm mt-2 mt-sm-0">
          <Row className="justify-content-end">
            <Col className="col-8 col-sm-8 col-lg-5 col-xl-4">
              <Search
                onSearch={onSearch}
                handleSearchChange={(searchText) => {
                  setSearchText(searchText);
                }}
                placeholder="Search by name or address"
                value={searchText}
              />
            </Col>
            <Col className="col-4 col-sm-auto">
              <ButtonWithBadge
                badgeValue={filterBadgeValue}
                text="Filter"
                icon="filter_alt"
                onClick={() => setShowFilteringSidePanel(true)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {solarPlantStatuses.length > 0 ? (
        <Row className="mt-4">
          <Col>
            <SitesErrorLogTable
              headers={[
                { id: 1, columnName: "Start Time" },
                { id: 2, columnName: "Site" },
                { id: 3, columnName: "Description" },
                { id: 4, columnName: "Severity" },
                { id: 5, columnName: "Status" },
              ]}
              data={solarPlantStatuses.map((item) =>
                mapErrorLogRow(
                  item,
                  getServiceProvider(item.generationServiceProviderId),
                  navigate,
                ),
              )}
            />
          </Col>
        </Row>
      ) : (
        <div className="container-dash d-flex align-items-center justify-content-center text-light mt-4 font-size-14">
          There are no error logs
        </div>
      )}
      <SitesErrorLogFilterPanel
        show={showFilteringSidePanel}
        onClose={() => setShowFilteringSidePanel(false)}
        updateFilters={updateFilters}
        setFilterBadgeValue={setFilterBadgeValue}
        filters={filters}
        solarBrandList={solarBrandList}
        alertOptions={alertOptions}
      />
      <SpinnerModal show={isFetchingSolarPlantStatuses} />
    </div>
  );
};

export default SitesErrorLog;
